/**
 * Body composition from one bioimpedance reading.
 *
 * A scale cannot see fat. It measures weight and the resistance of a small
 * current sent up one leg and down the other, and everything else on the box —
 * fat, water, muscle — is a regression fitted to that resistance plus the body
 * it travelled through. So the numbers here need the profile: the same 500 Ω
 * means something different in a tall body than a short one.
 *
 * The S400 sends two resistances rather than one. At 50 kHz the current cannot
 * cross cell membranes and flows only through the fluid between cells, which is
 * a longer way round and therefore the *higher* reading; at 250 kHz it passes
 * through the cells too, and reads lower. The difference between them is the
 * only thing in a weigh-in that says anything about where the water is, which
 * is what `ecwL` and `icwL` below are.
 *
 * Lineage: the lean-mass regression is openScale's reverse engineering of
 * Xiaomi's firmware (oliexdev/openScale via lolouk44/xiaomi_mi_scale). What
 * sits on top of it follows dckiller51/bodymiscale's "S400" engine (Apache-2.0)
 * rather than its "Xiaomi legacy" one, because the legacy path is the 2017 Zepp
 * Life formula and carries a 0.8 kg fudge in the fat calculation that the
 * dual-frequency hardware does not want — on a 78 kg body that constant alone
 * was the whole of a one-point disagreement with what the scale displays.
 *
 * Two honest limits, worth remembering before any of this is quoted at a user.
 * Foot-to-foot current never reaches the trunk, so the upper body is inferred
 * rather than measured, and athletic builds read a few points fatter than they
 * are — a bias the vendor's own app shares, because it is the geometry rather
 * than the arithmetic. And impedance tracks hydration, so a single reading is
 * noise; the trend across fasted mornings is the signal. Weight is the only
 * number here measured rather than modelled.
 *
 * The water compartments carry a third limit of their own, flagged where they
 * are computed: no peer-reviewed equation exists for dual-frequency foot-to-foot
 * hardware, and their constants are empirical. They are a trend, not a reading.
 */

import { round, type Sex } from './macros.js'

export interface BiaInput {
  weightKg: number
  /**
   * The 50 kHz resistance — the one every figure here is derived from, and the
   * only one a single-frequency scale sends.
   */
  impedanceOhm: number
  /**
   * The 250 kHz resistance. Absent when the scale never completed the barefoot
   * measurement, and on any bridge built before it was captured; everything
   * except the water compartments is computed without it.
   */
  impedanceHighOhm?: number | null
  heightCm: number
  ageYears: number
  sex: Sex
}

export interface BodyComposition {
  bodyFatPct: number
  waterPct: number
  muscleKg: number
  /** Protein as a share of body weight, a stable fraction of lean mass. */
  proteinPct: number
  /** Litres of water outside the cells. Null unless both frequencies arrived. */
  ecwL: number | null
  /** Litres of water inside them. Null unless both frequencies arrived. */
  icwL: number | null
}

/**
 * The figures that need no new measurement — arithmetic on a stored panel.
 *
 * None of these is a column. Every one is a function of weight, the fat
 * figure, the muscle figure and the profile, so a phone can compute them for
 * the whole history from what it already holds, and a formula that changes
 * changes them everywhere at once instead of leaving a step in the chart at
 * the day somebody thought of it.
 */
export interface BodyDerived {
  /** Everything that is not fat: muscle, bone, organs, water. Kilograms. */
  leanMassKg: number
  /** Xiaomi's "bone mineral". Kilograms, from the same lean-mass fit. */
  boneKg: number
  /**
   * Resting burn from lean mass (Katch-McArdle). Sits beside, not instead
   * of, the Mifflin-St Jeor figure the targets run on: this one knows what the
   * weight is made of, that one does not, and on a muscular body they differ.
   */
  bmrKcal: number
  /**
   * The age whose textbook resting burn matches this body's measured one.
   * Below the real age means the lean mass is carrying more than its years
   * would predict. A comparison, not a diagnosis.
   */
  metabolicAge: number
  /** Xiaomi's nine-class grid of fat band against muscle band. */
  bodyType: BodyType
  /** The fat band the profile's age and sex put "normal" at, for the label. */
  fatNormalPct: { from: number; to: number }
}

export type BodyType =
  | 'obese'
  | 'overweight'
  | 'thick_set'
  | 'lack_exercise'
  | 'balanced'
  | 'balanced_muscular'
  | 'skinny'
  | 'balanced_skinny'
  | 'skinny_muscular'

export interface DerivedInput {
  weightKg: number
  bodyFatPct: number
  muscleKg: number
  heightCm: number
  ageYears: number
  sex: Sex
}

/**
 * Outside this band the reading is not a body.
 *
 * A scale that never completed the barefoot measurement reports 0, and one
 * measuring through socks reports thousands. Both are "no impedance" rather
 * than "a very lean person", and the difference matters: a clamped nonsense
 * value would look exactly like a real one on the chart.
 */
const IMPEDANCE_MIN = 100
const IMPEDANCE_MAX = 3000

export function impedanceIsPlausible(impedanceOhm: number): boolean {
  return (
    Number.isFinite(impedanceOhm) && impedanceOhm >= IMPEDANCE_MIN && impedanceOhm <= IMPEDANCE_MAX
  )
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min
  if (value > max) return max
  return value
}

/**
 * Sort the two readings by physics rather than by the name they arrived under.
 *
 * Low frequency is always the higher resistance, because the current is taking
 * the long way around the cells. Decoders disagree about which of the two to
 * call "low" — ours names them after the frequency, the Home Assistant
 * integration after the magnitude, and getting it backwards would quietly move
 * every number — so neither name is trusted here and the values are ordered
 * instead. With one reading it is the 50 kHz one: that is the packet the scale
 * sends alongside the weight.
 */
function frequencies(input: BiaInput): { zLf: number; zHf: number | null } {
  const other = input.impedanceHighOhm
  if (other == null || !impedanceIsPlausible(other)) {
    return { zLf: input.impedanceOhm, zHf: null }
  }
  return {
    zLf: Math.max(input.impedanceOhm, other),
    zHf: Math.min(input.impedanceOhm, other),
  }
}

/**
 * Lean body mass, the quantity every other figure is derived from.
 *
 * Impedance enters here and nowhere else: subtracting it is what turns a
 * height-and-weight guess into a measurement.
 */
function leanBodyMass(input: BiaInput, zLf: number): number {
  const { weightKg, heightCm, ageYears, sex } = input
  let lbm =
    ((heightCm * 9.058) / 100) * (heightCm / 100) +
    weightKg * 0.32 +
    12.226 -
    zLf * 0.0068 -
    ageYears * 0.0542

  // The regression has no sex term at all, because the firmware it was lifted
  // from applied the correction later, inside its fat calculation. Taking the
  // straight physiological route to fat instead means the correction has to
  // move up here or women come out roughly sixteen percent too lean —
  // essential fat and skeletal muscle differ that much at the same height,
  // weight and resistance.
  if (sex === 'female') lbm *= 0.84

  // Nobody is one hundred percent lean. The ceiling stops a bad resistance
  // reading from producing a negative fat mass further down.
  return Math.min(lbm, weightKg * 0.98)
}

/**
 * Fat is what is left when lean mass is taken off the scale.
 *
 * Deliberately the plain subtraction rather than the Zepp Life variant, which
 * shaves a flat 0.8 kg off lean mass first. That constant is a fitted quirk of
 * the 2017 single-frequency lineage, and on a 78 kg body it is worth a full
 * point of body fat — the difference between agreeing with the scale on the
 * shelf and disagreeing with it every morning.
 */
function fatPercentage(input: BiaInput, lbm: number): number {
  const { weightKg, sex } = input
  const fat = ((weightKg - lbm) / weightKg) * 100
  // A biological floor, not a modelling one: below this a body has run out of
  // the fat it needs to work, so a lower number is a bad reading.
  return clamp(fat, sex === 'female' ? 10 : 5, 75)
}

/**
 * Water as a share of body weight (Pace & Rathbun 1945).
 *
 * Lean tissue is about 73 % water and fat is nearly none, so the fat figure
 * carries this one entirely. The ceiling is that same constant: no body can be
 * a larger share water than its lean tissue is.
 */
function waterPercentage(fatPct: number): number {
  return clamp((100 - fatPct) * 0.73, 35, 73)
}

function boneMass(sex: Sex, lbm: number): number {
  const base = sex === 'female' ? 0.245691014 : 0.18016894
  let bone = (base - lbm * 0.05158) * -1
  bone += bone > 2.2 ? 0.1 : -0.1

  const ceiling = sex === 'female' ? 5.1 : 5.2
  return clamp(bone > ceiling ? 8 : bone, 0.5, 8)
}

function muscleMass(input: BiaInput, fatPct: number, bone: number): number {
  const { weightKg, sex } = input
  const muscle = weightKg - fatPct * 0.01 * weightKg - bone

  const ceiling = sex === 'female' ? 84 : 93.5
  return clamp(muscle >= ceiling ? 120 : muscle, 10, 120)
}

/** Protein sits at a stable ~19.5 % of lean mass in healthy adults (Wang 1999). */
function proteinPercentage(weightKg: number, lbm: number): number {
  return clamp(((lbm * 0.195) / weightKg) * 100, 5, 35)
}

/**
 * Where the body's water sits — the one thing the second frequency buys.
 *
 * The ratio between the two resistances stands in for how easily current
 * crosses cell membranes, which tracks how the water divides between inside
 * and outside them. Read the caveat before showing either number to anyone:
 * the clinical equations for this were all validated on devices with
 * electrodes on the hands as well as the feet, none exists for a bathroom
 * scale, and the two constants below are fitted to land healthy adults in the
 * 37–39 % range rather than derived from anything. A drift over weeks means
 * something; a single figure does not.
 */
function waterCompartments(
  weightKg: number,
  fatPct: number,
  zLf: number,
  zHf: number,
): { ecwL: number; icwL: number } {
  // Total body water in litres, from the fat figure rather than from a second
  // impedance regression — the usual Deurenberg route overestimates badly
  // below a BMI of about 28, and every error in it would land in both
  // compartments.
  const tbwL = (1 - fatPct / 100) * 0.73 * weightKg
  const ecwL = tbwL * (0.32 + 0.08 * (zHf / zLf))
  return { ecwL, icwL: Math.max(0, tbwL - ecwL) }
}

/**
 * The whole panel, or nothing.
 *
 * Returns `null` rather than a partial result when the profile is incomplete or
 * the impedance is not a body: the caller then stores the weight alone, which
 * is honest, instead of a fat percentage computed from a default height. The
 * water compartments are the one part allowed to be absent on their own, since
 * a scale that sent a single frequency still measured everything else.
 */
export function bodyComposition(input: BiaInput): BodyComposition | null {
  const { weightKg, heightCm, ageYears } = input

  if (!Number.isFinite(weightKg) || weightKg <= 0) return null
  if (!Number.isFinite(heightCm) || heightCm < 50 || heightCm > 260) return null
  if (!Number.isFinite(ageYears) || ageYears < 10 || ageYears > 130) return null
  if (!impedanceIsPlausible(input.impedanceOhm)) return null

  const { zLf, zHf } = frequencies(input)
  const lbm = leanBodyMass(input, zLf)
  const bodyFatPct = fatPercentage(input, lbm)
  const bone = boneMass(input.sex, lbm)
  const compartments = zHf == null ? null : waterCompartments(weightKg, bodyFatPct, zLf, zHf)

  // Rounded to what the columns hold: the percentages are numeric(4,1),
  // muscle_kg numeric(5,2), the two compartments numeric(4,1). Storing more
  // digits would only promise a precision the method does not have.
  return {
    bodyFatPct: round(bodyFatPct, 1),
    waterPct: round(waterPercentage(bodyFatPct), 1),
    muscleKg: round(muscleMass(input, bodyFatPct, bone), 2),
    proteinPct: round(proteinPercentage(weightKg, lbm), 1),
    ecwL: compartments == null ? null : round(compartments.ecwL, 1),
    icwL: compartments == null ? null : round(compartments.icwL, 1),
  }
}

/**
 * The share of body water sitting outside the cells.
 *
 * Healthy adults land around 37–39 %. Above that can mean retained fluid or
 * inflammation, below it dehydration — but only as a movement against that
 * person's own baseline, never as a diagnosis from one morning.
 */
export function ecwShare(ecwL: number | null, icwL: number | null): number | null {
  if (ecwL == null || icwL == null) return null
  const tbw = ecwL + icwL
  if (tbw <= 0) return null
  return round((ecwL / tbw) * 100, 1)
}

// ─── Derived from a stored panel ─────────────────────────────────────────────

/**
 * Xiaomi's fat bands: [very low, low, normal, high] by age and sex — the
 * thresholds Mi Fitness colours its bar with, as transcribed by bodymiscale.
 * "Normal" runs from the second value to the third.
 */
const FAT_BANDS: ReadonlyArray<{
  from: number
  to: number
  female: readonly [number, number, number, number]
  male: readonly [number, number, number, number]
}> = [
  { from: 0, to: 12, female: [12, 21, 30, 34], male: [7, 16, 25, 30] },
  { from: 12, to: 14, female: [15, 24, 33, 37], male: [7, 16, 25, 30] },
  { from: 14, to: 16, female: [18, 27, 36, 40], male: [7, 16, 25, 30] },
  { from: 16, to: 18, female: [20, 28, 37, 41], male: [7, 16, 25, 30] },
  { from: 18, to: 40, female: [21, 28, 35, 40], male: [11, 17, 22, 27] },
  { from: 40, to: 60, female: [22, 29, 36, 41], male: [12, 18, 23, 28] },
  { from: 60, to: 101, female: [23, 30, 37, 42], male: [14, 20, 25, 30] },
]

/** Muscle bands [low, normal] by minimum height and sex, same source. */
const MUSCLE_BANDS: ReadonlyArray<{
  minHeight: { male: number; female: number }
  female: readonly [number, number]
  male: readonly [number, number]
}> = [
  { minHeight: { male: 170, female: 160 }, female: [36.5, 42.6], male: [49.4, 59.5] },
  { minHeight: { male: 160, female: 150 }, female: [32.9, 37.6], male: [44.0, 52.5] },
  { minHeight: { male: 0, female: 0 }, female: [29.1, 34.8], male: [38.5, 46.6] },
]

const BODY_TYPES: readonly BodyType[] = [
  'obese',
  'overweight',
  'thick_set',
  'lack_exercise',
  'balanced',
  'balanced_muscular',
  'skinny',
  'balanced_skinny',
  'skinny_muscular',
]

function fatBand(ageYears: number, sex: Sex): readonly [number, number, number, number] {
  const age = Math.trunc(ageYears)
  const row = FAT_BANDS.find((b) => age >= b.from && age < b.to) ?? FAT_BANDS[FAT_BANDS.length - 1]!
  return sex === 'female' ? row.female : row.male
}

function muscleBand(heightCm: number, sex: Sex): readonly [number, number] {
  const row =
    MUSCLE_BANDS.find((b) => heightCm >= b.minHeight[sex]) ?? MUSCLE_BANDS[MUSCLE_BANDS.length - 1]!
  return sex === 'female' ? row.female : row.male
}

/**
 * Where "normal" body fat sits for this age and sex, for the label beside the
 * figure. Xiaomi's bands, so the sheet and the scale's own app agree about
 * which colour the number is.
 */
export function fatNormalRange(ageYears: number, sex: Sex): { from: number; to: number } {
  const band = fatBand(ageYears, sex)
  return { from: band[1], to: band[2] }
}

/**
 * The rest of Mi Fitness's panel, from what is already stored.
 *
 * Lean mass is taken back off the rounded fat figure rather than recomputed
 * from ohms, so this agrees with the fat percentage a person is looking at to
 * the last digit; the rounding costs under fifty grams. Returns null when the
 * profile cannot support it — the same rule as `bodyComposition`.
 *
 * Metabolic age follows bodymiscale's S400 engine: the real age scaled by the
 * ratio of the textbook resting burn for that age (Harris-Benedict, revised)
 * to the measured one from lean mass. Truncated, then held between 12 and
 * twenty-five years over the real age, exactly as the reference does.
 */
export function bodyDerived(input: DerivedInput): BodyDerived | null {
  const { weightKg, bodyFatPct, muscleKg, heightCm, ageYears, sex } = input
  if (!Number.isFinite(weightKg) || weightKg <= 0) return null
  if (!Number.isFinite(bodyFatPct) || bodyFatPct < 0 || bodyFatPct >= 100) return null
  if (!Number.isFinite(heightCm) || heightCm < 50 || heightCm > 260) return null
  if (!Number.isFinite(ageYears) || ageYears < 10 || ageYears > 130) return null

  const lbm = weightKg * (1 - bodyFatPct / 100)
  const bmr = clamp(370 + 21.6 * lbm, 500, 5000)

  const expected =
    sex === 'male'
      ? 88.362 + 13.397 * weightKg + 4.799 * heightCm - 5.677 * ageYears
      : 447.593 + 9.247 * weightKg + 3.098 * heightCm - 4.33 * ageYears
  const rawAge = bmr > 0 ? ageYears * (expected / bmr) : ageYears
  const metabolicAge = clamp(Math.trunc(rawAge), 12, Math.min(95, Math.trunc(ageYears) + 25))

  const fat = fatBand(ageYears, sex)
  const muscle = muscleBand(heightCm, sex)
  // Fat: above the normal band scores 0, below it 2, inside it 1. Muscle:
  // above its band 2, below 0, inside 1. The grid reads fat first.
  const fatFactor = bodyFatPct > fat[2] ? 0 : bodyFatPct < fat[1] ? 2 : 1
  const muscleFactor = muscleKg > muscle[1] ? 2 : muscleKg < muscle[0] ? 0 : 1

  return {
    leanMassKg: round(lbm, 2),
    boneKg: round(boneMass(sex, lbm), 2),
    bmrKcal: Math.round(bmr),
    metabolicAge,
    bodyType: BODY_TYPES[muscleFactor + fatFactor * 3]!,
    fatNormalPct: { from: fat[1], to: fat[2] },
  }
}
